import { defined } from "@entrilia/shared/utilities/typeHelper";

export const MAX_IMAGE_COUNT = 4;
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 1600;
export const IMAGE_JPEG_QUALITY = 0.9;

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface ImageAttachmentSelection {
  accepted: File[];
  rejectedMessages: string[];
}

const isImageFile = (file: File): boolean => file.type.startsWith("image/");

const describeImageTypeError = (file: File): string => `"${file.name}" isn't an image.`;

const describeImageCountError = (): string => `You can attach up to ${MAX_IMAGE_COUNT} images.`;

export const describeImageSizeError = (file: File): string =>
  `"${file.name}" is still larger than ${MAX_IMAGE_BYTES / (1024 * 1024)} MB after resizing.`;

// Files are checked in order so the count limit only rejects the ones past the cap, not the whole drop.
export const selectImagesToAttach = (currentCount: number, files: File[]): ImageAttachmentSelection => {
  const accepted: File[] = [];
  const rejectedMessages: string[] = [];
  let count = currentCount;
  for (const file of files) {
    if (!isImageFile(file)) {
      rejectedMessages.push(describeImageTypeError(file));
      continue;
    }
    if (count >= MAX_IMAGE_COUNT) {
      rejectedMessages.push(describeImageCountError());
      continue;
    }
    accepted.push(file);
    count += 1;
  }
  return { accepted, rejectedMessages: [...new Set(rejectedMessages)] };
};

export const isWithinImageSizeLimit = (byteLength: number): boolean => byteLength <= MAX_IMAGE_BYTES;

export const getResizedImageDimensions = (
  { width, height }: ImageDimensions,
  maxSide = MAX_IMAGE_DIMENSION
): ImageDimensions => {
  const longSide = Math.max(width, height);
  if (longSide <= maxSide) {
    return { width, height };
  }
  const scale = maxSide / longSide;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
};

export const hasTransparentPixels = (pixels: ArrayLike<number>): boolean => {
  for (let i = 3; i < pixels.length; i += 4) {
    if (defined(pixels[i]) < 255) {
      return true;
    }
  }
  return false;
};
